import { HEALTH_QUOTE_STEP_KEYS } from "@/D2C/features/health-quote/common/static";
import { saveHealthQuoteStepFor } from "@/D2C/features/health-quote/redux/health-quote/healthQuoteSlice";
import {
  createHealthSession,
  updateHealthSessionStep,
} from "@/D2C/features/health-quote/services/service";
import {
  buildHealthCreateSessionPayload,
  buildHealthQuoteRequestBody,
} from "@/D2C/features/health-quote/utils/healthQuotePayloadsUtils";
import { App_Url_New, LOB_IDS } from "@/shared/common/static";
import { SESSION_STEP_KEYS_BY_JOURNEY } from "@/shared/constants/sessionStepKeys";
import { setSession } from "@/shared/redux/reducers/authSlice";
import { clearQuoteList, setQuoteList, sortQuotes } from "@/shared/redux/reducers/commonSlice";
import { setSharedPersonalDetails } from "@/shared/redux/reducers/personalDetailsSlice";
import type { AppDispatch } from "@/shared/redux/store/store";
import { streamQuotes } from "@/shared/services/quoteStreamService";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { getSessionIds, type SessionIds } from "../utils/getSessionIds";
import type { useQuoteRefs } from "./useQuoteRefs";

type Refs = ReturnType<typeof useQuoteRefs>;

type HealthInvalidSections = {
  basic: boolean;
  habits: boolean;
  quick: boolean;
};

export const useHealthQuotes = (
  refs: Pick<
    Refs,
    "healthPersonalRef" | "healthDetailsRef" | "healthHabitsRef" | "healthQuickQuestionsRef"
  >,
  setActiveSessionIds: (ids: SessionIds) => void,
  onValidationFailed?: (invalidSections: HealthInvalidSections) => void
) => {
  const dispatch = useDispatch<AppDispatch>();

  return useCallback(async () => {
    const personalValid = await refs.healthPersonalRef.current?.submit();
    const detailsValid = await refs.healthDetailsRef.current?.submit();
    const habitsValid = await refs.healthHabitsRef.current?.submit();
    const questionsValid = await refs.healthQuickQuestionsRef.current?.submit();
    if (!personalValid || !detailsValid || !habitsValid || !questionsValid) {
      onValidationFailed?.({
        basic: !detailsValid,
        habits: !habitsValid,
        quick: !questionsValid,
      });
      return false;
    }

    const personalData = refs.healthPersonalRef.current!.getValues();
    const healthDetails = refs.healthDetailsRef.current!.getValues();
    const healthHabits = refs.healthHabitsRef.current!.getValues();
    const quickQuestions = refs.healthQuickQuestionsRef.current!.getValues();

    const responseData = await createHealthSession({
      step: SESSION_STEP_KEYS_BY_JOURNEY.HEALTH.PERSONAL_DETAILS,
      ...buildHealthCreateSessionPayload(personalData),
    });
    const ids = getSessionIds(responseData);
    setActiveSessionIds(ids);

    const requestBody = buildHealthQuoteRequestBody(
      personalData as any,
      healthDetails,
      healthHabits,
      quickQuestions,
      ids.session
    );

    dispatch(setSession(responseData));
    dispatch(setSharedPersonalDetails(personalData as any));
    dispatch(saveHealthQuoteStepFor(HEALTH_QUOTE_STEP_KEYS.PERSONAL_DETAILS, personalData));
    dispatch(saveHealthQuoteStepFor(HEALTH_QUOTE_STEP_KEYS.HEALTH_DETAILS, healthDetails));
    dispatch(saveHealthQuoteStepFor(HEALTH_QUOTE_STEP_KEYS.HEALTH_HABITS, healthHabits));
    dispatch(saveHealthQuoteStepFor(HEALTH_QUOTE_STEP_KEYS.QUICK_QUESTIONS, quickQuestions));

    if (ids.clientId && ids.sessionId) {
      await updateHealthSessionStep({
        step: SESSION_STEP_KEYS_BY_JOURNEY.HEALTH.HEALTH_DETAILS,
        payload: {
          health_details: requestBody.health_details,
          personal_details: requestBody.personal_details,
        },
        start_date: requestBody.health_details.policy_dates.start_date || null,
        end_date: requestBody.health_details.policy_dates.end_date || null,
        clientId: ids.clientId,
        sessionId: ids.sessionId,
        lobId: LOB_IDS.HEALTH,
      });

      await updateHealthSessionStep({
        step: SESSION_STEP_KEYS_BY_JOURNEY.HEALTH.HEALTH_HABITS,
        payload: {
          health_details: requestBody.health_details,
          personal_details: requestBody.personal_details,
          health_habits: requestBody.health_habits,
        },
        clientId: ids.clientId,
        sessionId: ids.sessionId,
        lobId: LOB_IDS.HEALTH,
      });

      await updateHealthSessionStep({
        step: SESSION_STEP_KEYS_BY_JOURNEY.HEALTH.QUICK_QUESTIONS,
        payload: {
          health_details: requestBody.health_details,
          personal_details: requestBody.personal_details,
          health_habits: requestBody.health_habits,
          quick_questions: requestBody.quick_questions,
          medical_conditions: requestBody.medical_conditions,
          lifestyle_details: requestBody.lifestyle_details,
        },
        clientId: ids.clientId,
        sessionId: ids.sessionId,
        lobId: LOB_IDS.HEALTH,
      });
    }

    const collectedQuotes: any[] = [];
    dispatch(clearQuoteList());
    await streamQuotes({
      endpointPath: App_Url_New.ApiEndPoint.healthGetQuote,
      requestBody,
      onQuoteReceived: (quote) => {
        dispatch(setQuoteList(quote as any));
        dispatch(sortQuotes("sequence-asc"));
        if (quote?.insurer?.name && quote?.plan?.name) collectedQuotes.push(quote);
      },
      onStreamComplete: () => {
        if (!ids.clientId || !ids.sessionId || collectedQuotes.length === 0) return;
        void updateHealthSessionStep({
          step: "quote_request",
          payload: {
            health_details: requestBody.health_details,
            personal_details: requestBody.personal_details,
            quotes: collectedQuotes,
          },
          clientId: ids.clientId,
          sessionId: ids.sessionId,
          lobId: LOB_IDS.HEALTH,
        });
      },
      onStreamError: (err) => console.error("Health quote stream error:", err),
    });
    dispatch(sortQuotes("sequence-asc"));
    return true;
  }, [dispatch, onValidationFailed, refs, setActiveSessionIds]);
};
